import PropTypes from "prop-types"
import React, { useContext, useMemo, useState } from "react"

import Button from "src/components/Button"
import Divider from "src/components/Divider"
import FileUpload from "src/components/FileUpload"
import Heading from "src/components/Heading"

import { validateDocument } from "src/api/Contracts/SignContract"

import { parseDateOnly } from "src/utils/date_only"

import ReachOut from "../ReachOut"
import { SignContractWizardContext } from "../SignContractWizardContext"
import RequirementsCard from "./InsuranceAndRegistration/RequirementsCard"
import RequiredFormInput, { recursivelySet } from "./RequiredFormInput"
import fileUploadCopy from "./fileUploadCopy"
import { evaluateRequiredStep } from "./requiredStepValidation"

const resolveDocumentError = (result, wizardCopy) =>
  result.code === "forbidden" ? wizardCopy.uploadForbidden : result.error

const InsuranceAndRegistration = ({ form }) => {
  const { agreementOverview } = useContext(SignContractWizardContext)

  if (agreementOverview) return <InsuranceAndRegistrationRedesign form={form} />

  return <InsuranceAndRegistrationLegacy form={form} />
}

InsuranceAndRegistration.propTypes = {
  form: PropTypes.object.isRequired,
}

const InsuranceAndRegistrationRedesign = ({ form }) => {
  const {
    state,
    dispatch,
    quote,
    marina,
    reachOut,
    contractChatUrl,
    wizardCopy,
    steps,
  } = useContext(SignContractWizardContext)
  const { requiredInformation } = state
  const { insurance, registration } = requiredInformation

  const nextStep = steps[state.currentStep]

  const {
    insuranceRequired,
    registrationRequired,
    insuranceRequirement,
    registrationRequirement,
  } = quote

  const minDate = useMemo(() => parseDateOnly(quote.marinaToday), [quote])

  const uploadCopy = useMemo(() => fileUploadCopy(wizardCopy), [wizardCopy])

  const [attemptedContinue, setAttemptedContinue] = useState(false)

  const { valid, messages, warnings } = useMemo(
    () => evaluateRequiredStep(state, quote, { includePhone: false }),
    [state, quote]
  )

  const insuranceDocumentUploadRequired =
    insuranceRequired && insuranceRequirement === "info_and_upload"
  const registrationDocumentUploadRequired =
    registrationRequired && registrationRequirement === "info_and_upload"

  const insuranceFile = form.watch("insurance.document")?.[0] ?? null
  const registrationFile = form.watch("registration.document")?.[0] ?? null

  const setDocument = (type, document, documentError = null, extra = {}) =>
    dispatch({
      type: "REQUIRED_INFORMATION_CHANGED",
      payload: recursivelySet(type, { document, documentError, ...extra }),
    })

  const acceptDocument = (type, file) => {
    form.setValue(`${type}.document`, [file])
    setDocument(type, true, null, { replacingDocument: false })
  }

  const rejectDocument = (type, error) => {
    form.setValue(`${type}.document`, null)
    setDocument(type, false, error)
  }

  const startReplacing = (type) => {
    form.setValue(`${type}.document`, null)
    setDocument(type, false, null, { replacingDocument: true })
  }

  const cancelReplacing = (type) =>
    dispatch({
      type: "REQUIRED_INFORMATION_CHANGED",
      payload: recursivelySet(type, {
        replacingDocument: false,
        documentError: null,
      }),
    })

  const handleContinue = async () => {
    const validRhf = await form.trigger()
    setAttemptedContinue(true)
    if (validRhf && valid) dispatch({ type: "GO_TO_NEXT_STEP" })
  }

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <Heading.HeroTitle>{wizardCopy.insuranceTitle}</Heading.HeroTitle>
        <p className="text-base font-semibold text-gray-700">
          {wizardCopy.insuranceSubtitle}
        </p>
      </div>

      <div className="hidden lg:block [&>hr]:my-0">
        <Divider />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="flex flex-col space-y-6 lg:col-span-2">
          {insuranceRequired && (
            <section id="insurance" role="region" aria-label="Insurance">
              <h4 className="mb-4 mt-0 font-semibold text-gray-700">
                Insurance
              </h4>
              {insuranceDocumentUploadRequired && (
                <div className="mb-4">
                  <FileUpload
                    label={wizardCopy.insuranceUpload}
                    accept="image/jpg, image/jpeg, image/png, application/pdf"
                    copy={uploadCopy}
                    existingDocument={insurance.existingDocument}
                    value={insuranceFile}
                    error={
                      insurance.documentError ||
                      (attemptedContinue
                        ? messages["insurance.document"]
                        : null)
                    }
                    replacing={insurance.replacingDocument}
                    onValidate={(file) =>
                      validateDocument({
                        quoteId: quote.encodedId,
                        file,
                        documentType: "insurance",
                      }).then((result) => ({
                        ...result,
                        error: resolveDocumentError(result, wizardCopy),
                      }))
                    }
                    onAccepted={(file) => acceptDocument("insurance", file)}
                    onRejected={(error) => rejectDocument("insurance", error)}
                    onReplace={() => startReplacing("insurance")}
                    onCancelReplace={() => cancelReplacing("insurance")}
                  />
                </div>
              )}
              <RequiredFormInput
                inputKey="insurance.companyName"
                label="Company Name"
                value={insurance.companyName}
                form={form}
                type="text"
                message={messages["insurance.companyName"]}
              />
              <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2">
                <RequiredFormInput
                  inputKey="insurance.policyNumber"
                  label="Policy Number"
                  value={insurance.policyNumber}
                  form={form}
                  type="text"
                  message={messages["insurance.policyNumber"]}
                />
                <RequiredFormInput
                  inputKey="insurance.expirationDate"
                  label="Expiration Date"
                  value={insurance.expirationDate}
                  form={form}
                  type="date"
                  message={messages["insurance.expirationDate"]}
                  warning={warnings["insurance.expirationDate"]}
                  minDate={minDate}
                />
              </div>
            </section>
          )}

          {registrationRequired && (
            <section id="registration" role="region" aria-label="Registration">
              <h4 className="mb-4 mt-0 font-semibold text-gray-700">
                Registration
              </h4>
              {registrationDocumentUploadRequired && (
                <div className="mb-4">
                  <FileUpload
                    label={wizardCopy.registrationUpload}
                    accept="image/jpg, image/jpeg, image/png, application/pdf"
                    copy={uploadCopy}
                    existingDocument={registration.existingDocument}
                    value={registrationFile}
                    error={
                      registration.documentError ||
                      (attemptedContinue
                        ? messages["registration.document"]
                        : null)
                    }
                    replacing={registration.replacingDocument}
                    onValidate={(file) =>
                      validateDocument({
                        quoteId: quote.encodedId,
                        file,
                        documentType: "registration",
                      }).then((result) => ({
                        ...result,
                        error: resolveDocumentError(result, wizardCopy),
                      }))
                    }
                    onAccepted={(file) => acceptDocument("registration", file)}
                    onRejected={(error) =>
                      rejectDocument("registration", error)
                    }
                    onReplace={() => startReplacing("registration")}
                    onCancelReplace={() => cancelReplacing("registration")}
                  />
                </div>
              )}
              <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                <RequiredFormInput
                  inputKey="registration.registrationNumber"
                  label="Registration Number"
                  value={registration.registrationNumber}
                  form={form}
                  type="text"
                  message={messages["registration.registrationNumber"]}
                />
                <RequiredFormInput
                  inputKey="registration.expirationDate"
                  label="Expiration Date"
                  value={registration.expirationDate}
                  form={form}
                  type="date"
                  message={messages["registration.expirationDate"]}
                  warning={warnings["registration.expirationDate"]}
                  minDate={minDate}
                />
              </div>
            </section>
          )}

          <Button variant="primary" fullWidth onClick={handleContinue}>
            {nextStep
              ? `${wizardCopy.continueTo} ${nextStep.title}`
              : wizardCopy.continue}
          </Button>
        </div>

        <div className="flex flex-col space-y-4">
          <RequirementsCard
            insuranceRequired={insuranceRequired}
            registrationRequired={registrationRequired}
            copy={wizardCopy}
          />
          <ReachOut
            reachOut={reachOut}
            contractChatUrl={contractChatUrl}
            marinaId={marina.id}
            marinaName={marina.name}
            quoteId={quote.id}
          />
        </div>
      </div>
    </div>
  )
}

InsuranceAndRegistrationRedesign.propTypes = {
  form: PropTypes.object.isRequired,
}

const InsuranceAndRegistrationLegacy = ({ form }) => {
  const { state, dispatch, quote } = useContext(SignContractWizardContext)
  const { requiredInformation } = state

  const {
    insuranceRequired,
    registrationRequired,
    phoneInvalid,
    insuranceRequirement,
    registrationRequirement,
  } = quote

  const minDate = useMemo(() => parseDateOnly(quote.marinaToday), [quote])

  const { messages, warnings } = useMemo(
    () => evaluateRequiredStep(state, quote),
    [state, quote]
  )

  const insuranceDocumentUploadRequired =
    insuranceRequired && insuranceRequirement === "info_and_upload"
  const registrationDocumentUploadRequired =
    registrationRequired && registrationRequirement === "info_and_upload"

  return (
    <div className="flex flex-col space-y-6">
      {phoneInvalid && (
        <Contact
          phone={requiredInformation.phone}
          dispatch={dispatch}
          form={form}
          message={messages.phone}
        />
      )}
      {insuranceRequired && (
        <Insurance
          insurance={requiredInformation.insurance}
          dispatch={dispatch}
          form={form}
          documentUploadRequired={insuranceDocumentUploadRequired}
          messages={messages}
          warnings={warnings}
          minDate={minDate}
        />
      )}
      {registrationRequired && (
        <Registration
          registration={requiredInformation.registration}
          dispatch={dispatch}
          form={form}
          documentUploadRequired={registrationDocumentUploadRequired}
          messages={messages}
          warnings={warnings}
          minDate={minDate}
        />
      )}
    </div>
  )
}

const Contact = ({ phone, form, dispatch, message }) => {
  return (
    <div id="contact" role="region" aria-label="Contact">
      <h4 className="mb-4">Contact Info</h4>
      <RequiredFormInput
        inputKey="phone"
        label="Phone"
        value={phone}
        form={form}
        message={message}
      />
    </div>
  )
}

Contact.propTypes = {
  phone: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  message: PropTypes.string,
}

const Insurance = ({
  insurance,
  form,
  documentUploadRequired,
  messages,
  warnings,
  minDate,
}) => {
  return (
    <div id="insurance" role="region" aria-label="Insurance">
      <h4>Insurance</h4>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <RequiredFormInput
          inputKey="insurance.policyNumber"
          label={"Policy Number"}
          value={insurance.policyNumber}
          form={form}
          type="text"
          message={messages["insurance.policyNumber"]}
        />
        <RequiredFormInput
          inputKey="insurance.companyName"
          label={"Company Name"}
          value={insurance.companyName}
          form={form}
          type="text"
          message={messages["insurance.companyName"]}
        />
        <RequiredFormInput
          inputKey="insurance.expirationDate"
          label={"Expiration Date"}
          value={insurance.expirationDate}
          form={form}
          type="date"
          message={messages["insurance.expirationDate"]}
          warning={warnings["insurance.expirationDate"]}
          minDate={minDate}
        />
      </div>
      {documentUploadRequired && (
        <DocumentUpload
          title="Upload Insurance Document"
          inputKey="insurance.document"
          form={form}
        />
      )}
    </div>
  )
}

Insurance.propTypes = {
  insurance: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  documentUploadRequired: PropTypes.bool.isRequired,
  messages: PropTypes.object.isRequired,
  warnings: PropTypes.object.isRequired,
  minDate: PropTypes.instanceOf(Date),
}

const Registration = ({
  registration,
  form,
  documentUploadRequired,
  messages,
  warnings,
  minDate,
}) => {
  return (
    <div id="registration" role="region" aria-label="Registration">
      <h4>Registration</h4>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <RequiredFormInput
          inputKey="registration.registrationNumber"
          label={"Registration Number"}
          value={registration.registrationNumber}
          form={form}
          type="text"
          message={messages["registration.registrationNumber"]}
        />
        <RequiredFormInput
          inputKey="registration.expirationDate"
          label={"Expiration Date"}
          value={registration.expirationDate}
          form={form}
          type="date"
          message={messages["registration.expirationDate"]}
          warning={warnings["registration.expirationDate"]}
          minDate={minDate}
        />
      </div>
      {documentUploadRequired && (
        <DocumentUpload
          title="Upload Registration Document"
          inputKey="registration.document"
          form={form}
        />
      )}
    </div>
  )
}

Registration.propTypes = {
  registration: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  documentUploadRequired: PropTypes.bool.isRequired,
  messages: PropTypes.object.isRequired,
  warnings: PropTypes.object.isRequired,
  minDate: PropTypes.instanceOf(Date),
}

const DocumentUpload = ({ title, inputKey, form }) => {
  const { dispatch, state, quote } = useContext(SignContractWizardContext)

  const { register, watch, resetField } = form
  const { onChange, onBlur, name, ref } = register(inputKey)
  const inputFileList = watch(inputKey)

  // Get existing document from state
  const documentType = inputKey.includes("insurance")
    ? "insurance"
    : "registration"
  const { existingDocument, documentError, documentValidating } =
    state.requiredInformation[documentType]

  let file
  const fileSelected = inputFileList !== undefined && inputFileList.length === 1
  if (fileSelected) {
    file = inputFileList[0]
  }

  // Track if user wants to replace existing document
  const [replacingDocument, setReplacingDocument] = useState(false)

  const handleFileSelected = async (event) => {
    onChange(event)
    const selected = event.target.files?.[0]
    if (!selected) return

    dispatch({
      type: "REQUIRED_INFORMATION_CHANGED",
      payload: recursivelySet(
        inputKey.replace(".document", ".documentValidating"),
        true
      ),
    })

    let result
    try {
      result = await validateDocument({
        quoteId: quote.encodedId,
        file: selected,
        documentType,
      })
    } catch (error) {
      dispatch({
        type: "REQUIRED_INFORMATION_CHANGED",
        payload: recursivelySet(documentType, {
          document: false,
          documentError: quote.wizardCopy.documentCheckFailed,
          documentValidating: false,
        }),
      })
      resetField(inputKey)
      return
    }

    dispatch({
      type: "REQUIRED_INFORMATION_CHANGED",
      payload: recursivelySet(documentType, {
        document: result.valid,
        documentError: result.valid
          ? null
          : resolveDocumentError(result, quote.wizardCopy),
        documentValidating: false,
      }),
    })

    if (!result.valid) resetField(inputKey)
  }

  const handleFileReplace = () => {
    resetField(inputKey)
    const payload = recursivelySet(inputKey, false)
    dispatch({ type: "REQUIRED_INFORMATION_CHANGED", payload })
    setReplacingDocument(false)
  }

  const handleReplaceExisting = () => {
    setReplacingDocument(true)
  }

  const handleCancelReplace = () => {
    setReplacingDocument(false)
    resetField(inputKey)
    dispatch({
      type: "REQUIRED_INFORMATION_CHANGED",
      payload: recursivelySet(documentType, {
        document: false,
        documentError: null,
      }),
    })
  }

  // If there's an existing document and we're not replacing it, show the existing document UI
  if (existingDocument && !replacingDocument && !fileSelected) {
    return (
      <div className="border-green-300 bg-green-50 flex cursor-default flex-col items-start space-y-3 rounded-lg border p-3">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center">
            <i className="fa-solid fa-check mr-2 text-teal-600" />
            <span className="font-medium text-gray-700">Document Attached</span>
          </div>
        </div>

        {existingDocument.previewUrl && (
          <div className="flex items-center space-x-4">
            <img
              src={existingDocument.previewUrl}
              alt="Document preview"
              className="h-32 w-32 rounded border border-gray-300 object-center"
            />
          </div>
        )}
        <div className="flex space-x-2">
          <a
            href={existingDocument.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-sm"
          >
            View
          </a>
          <Button small onClick={handleReplaceExisting} variant="secondary">
            Replace Document
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex cursor-default flex-col items-start space-y-3 rounded-lg border border-gray-300 p-3">
      <label htmlFor={`${title}-file`} className="mb-0 flex">
        {replacingDocument ? `Replace ${title}` : title}
        <span className="ml-0.5 text-red-600">*</span>
      </label>

      {fileSelected ? (
        <div className="flex items-center">
          <span className="mr-2 text-gray-600">{file?.name}</span>
          <Button
            small
            icon="fa-trash-can"
            iconStyle="solid"
            onClick={handleFileReplace}
            variant="ghost"
            iconOnly
          />
        </div>
      ) : (
        <>
          <input
            id={`${title}-file`}
            type="file"
            required={!existingDocument}
            onChange={handleFileSelected}
            onBlur={onBlur}
            name={name}
            ref={ref}
            accept={"image/jpg, image/jpeg, image/png, application/pdf"}
          />
          <span className="text-gray-600">
            PDF and image files accepted (pdf, png, or jpg).
          </span>

          {replacingDocument && (
            <Button small onClick={handleCancelReplace} variant="ghost">
              Cancel
            </Button>
          )}
        </>
      )}

      {documentValidating && (
        <div className="flex items-center text-gray-600">
          <i className="fa-solid fa-spinner fa-spin mr-2" />
          Validating document&hellip;
        </div>
      )}

      {documentError && !documentValidating && (
        <div className="flex flex-col">
          <span className="font-semibold text-red-600">{documentError}</span>
          <span className="text-gray-600">
            Select a different file to try again.
          </span>
        </div>
      )}
    </div>
  )
}

DocumentUpload.propTypes = {
  title: PropTypes.string.isRequired,
  inputKey: PropTypes.string.isRequired,
  file: PropTypes.shape({
    content: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
  }),
  form: PropTypes.object.isRequired,
}

InsuranceAndRegistrationLegacy.propTypes = {
  form: PropTypes.object.isRequired,
}

export default InsuranceAndRegistration
